import React, { useMemo, useState } from 'react';
import { Layer, Rectangle, ResponsiveContainer, Sankey, Tooltip } from 'recharts';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { MultiColumnVizProps } from '../types';
import { dsVar } from '../../../theme/tokens';
import { useThemeMode } from '../../../theme/ThemeModeProvider';
import MultiColumnTable from '../MultiColumnTable';
import {
    SANKEY_COLORS,
    SANKEY_COLORS_LIGHT,
    SankeyModel,
    SankeyNodeDatum,
    buildSankeyModel,
    formatAmount,
} from './sankeyModel';

const FONT = '"Inter", sans-serif';
const NODE_WIDTH = 12;
const ROW_HEIGHT = 44;

// ─── Node / link renderers (module scope: stable component identity) ─────────

interface NodeShapeProps {
    x: number;
    y: number;
    width: number;
    height: number;
    index: number;
    payload: SankeyNodeDatum & { value: number };
}

const NodeShape: React.FC<NodeShapeProps> = ({ x, y, width, height, payload }) => {
    // Payers are labelled into the left margin, the subject and payees to their right.
    const labelOnLeft = payload.side === 'source';
    const flag = payload.priorAlerts ? ` ⚑${payload.priorAlerts}` : '';
    return (
        <Layer>
            <Rectangle
                x={x} y={y} width={width} height={Math.max(height, 2)}
                fill={payload.dashed ? 'transparent' : payload.color}
                stroke={payload.color} strokeWidth={payload.dashed ? 1.5 : 0}
                strokeDasharray={payload.dashed ? '4 3' : undefined}
                fillOpacity={0.95}
            />
            <text
                x={labelOnLeft ? x - 8 : x + width + 8}
                y={y + height / 2}
                textAnchor={labelOnLeft ? 'end' : 'start'}
                dominantBaseline="middle"
                style={{ fontFamily: FONT, fontSize: 12, fill: payload.dashed ? dsVar('fg-2') : dsVar('fg-body'),
                         fontStyle: payload.dashed ? 'italic' : 'normal' }}
            >
                {payload.name}{flag}
                <tspan style={{ fill: dsVar('fg-2'), fontSize: 11 }}> {formatAmount(payload.value)}</tspan>
            </text>
        </Layer>
    );
};

interface LinkShapeProps {
    sourceX: number;
    targetX: number;
    sourceY: number;
    targetY: number;
    sourceControlX: number;
    targetControlX: number;
    linkWidth: number;
    payload: { source: SankeyNodeDatum; target: SankeyNodeDatum };
}

const LinkShape: React.FC<LinkShapeProps> = (props) => {
    const { sourceX, targetX, sourceY, targetY, sourceControlX, targetControlX, linkWidth, payload } = props;
    // Colour a flow by its counterparty: the end that is not the subject.
    const party = payload.source.side === 'subject' ? payload.target : payload.source;
    return (
        <path
            d={`M${sourceX},${sourceY} C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}`}
            fill="none"
            stroke={party.color}
            strokeOpacity={party.dashed ? 0.25 : 0.4}
            strokeWidth={Math.max(linkWidth, 1)}
            strokeDasharray={party.dashed ? '6 4' : undefined}
        />
    );
};

const SankeyTooltip: React.FC<{ active?: boolean; payload?: any[]; model: SankeyModel }> = ({ active, payload, model }) => {
    if (!active || !payload?.length) return null;
    const item = payload[0]?.payload?.payload ?? payload[0]?.payload;
    if (!item) return null;
    const isLink = item.source && item.target && typeof item.source === 'object';
    const sideTotal = item.side === 'source' ? model.inTotal : item.side === 'target' ? model.outTotal : 0;
    const rows: [string, React.ReactNode][] = isLink
        ? [
            ['Flow', `${item.source.name} → ${item.target.name}`],
            ['Amount', formatAmount(item.value)],
            ['Payments', item.payments ?? '—'],
            ['Share', item.share ?? '—'],
            ['Channel', item.channel ?? '—'],
            ['Risk', item.risk ?? '—'],
        ]
        : [
            ['Party', item.name],
            ['Total', `${formatAmount(item.value)}${sideTotal
                ? ` (${Math.round((item.value / sideTotal) * 100)}% of ${item.side === 'source' ? 'inflows' : 'outflows'})` : ''}`],
            ['Role', item.role],
            ...(item.riskLevel ? [['EDD risk', item.riskLevel] as [string, React.ReactNode]] : []),
            ...(item.priorAlerts ? [['Prior alerts', item.priorAlerts] as [string, React.ReactNode]] : []),
            ...(item.investigatedUnder ? [['Investigated under', item.investigatedUnder] as [string, React.ReactNode]] : []),
        ];
    return (
        <div style={{ background: dsVar('surface'), border: `1px solid ${dsVar('line')}`, borderRadius: 8,
                      padding: '10px 12px', fontSize: 12, fontFamily: FONT }}>
            {rows.map(([label, value]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 2 }}>
                    <span style={{ color: dsVar('fg-2') }}>{label}</span>
                    <span style={{ color: dsVar('fg-body') }}>{value}</span>
                </div>
            ))}
        </div>
    );
};

// ─── Chart ────────────────────────────────────────────────────────────────────

/**
 * Flow chart for SANKEY items: who pays the subject and whom it pays, by amount.
 * Triggered by: visualizationType = 'SANKEY'.
 *
 * Built from the item's table rows (metadata.source_param / target_param /
 * value_param), so the same rows are one click away as a table, and a backend that
 * predates this component still renders them as one.
 */
const SankeyChart: React.FC<MultiColumnVizProps> = (props) => {
    const { headers, data, metadata, itemLabel } = props;
    const [view, setView] = useState<'chart' | 'table'>('chart');
    const palette = useThemeMode().resolved === 'light' ? SANKEY_COLORS_LIGHT : SANKEY_COLORS;
    const model: SankeyModel | null = useMemo(
        () => buildSankeyModel(headers, data, metadata, palette),
        [headers, data, metadata, palette],
    );

    if (!model) return <MultiColumnTable {...props} />;

    const perSide = Math.max(
        model.nodes.filter(n => n.side === 'source').length,
        model.nodes.filter(n => n.side === 'target').length,
        1,
    );
    const height = Math.max(280, perSide * ROW_HEIGHT);
    const title = metadata.title || itemLabel;

    return (
        <div className="mb-8 p-6 bg-surface border border-line rounded-xl">
            <div className="flex items-center justify-between mb-4">
                {title ? (
                    <h3 className="text-accent-fg text-base font-mono flex items-center gap-2">
                        <AccountTreeIcon fontSize="small" />
                        {title}
                    </h3>
                ) : <span />}
                <div className="inline-flex rounded-lg border border-line overflow-hidden" role="group" aria-label="Fund flow view">
                    {(['chart', 'table'] as const).map(v => (
                        <button key={v} type="button" onClick={() => setView(v)}
                            className={`px-3 py-1 text-xs ${view === v ? 'bg-line text-accent-fg' : 'text-fg-2'}`}>
                            {v === 'chart' ? 'Flow' : 'Table'}
                        </button>
                    ))}
                </div>
            </div>
            {view === 'table' ? <MultiColumnTable {...props} /> : (
                <div style={{ height, width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <Sankey
                            data={{ nodes: model.nodes, links: model.links }}
                            nodeWidth={NODE_WIDTH}
                            nodePadding={18}
                            iterations={0}
                            margin={{ top: 8, right: 180, bottom: 8, left: 180 }}
                            node={(nodeProps: any) => <NodeShape {...nodeProps} />}
                            link={(linkProps: any) => <LinkShape {...linkProps} />}
                        >
                            <Tooltip content={<SankeyTooltip model={model} />} />
                        </Sankey>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
};

export default SankeyChart;
